// learner-store.js — localStorage-backed mastery progress (Brief 003).
//
// KFE Web persists to IndexedDB via a rich learner store (streaks, item
// progress, sessions, sync). The demo is a standalone single-player walkthrough,
// so the plan (Phase 3) explicitly allows a tiny localStorage map instead:
//
//   { [mmuId]: consecutiveCorrectStreak }
//
// Training records every tile attempt here; an MMU is "mastered" once its streak
// reaches the mastery target (default 3 — mirrors the engine's
// defaultMasteryStreak). Testing reads the mastered set as its pool (Brief 003
// success criterion 3). A wrong attempt resets the streak to 0, so a previously
// mastered MMU can fall back out of the Testing pool — faithful to the engine's
// streak rule.

const STORAGE_KEY = "l2b-demo:mastery";
const DEFAULT_MASTERY_TARGET = 3;

/**
 * Read the streak map from localStorage. Tolerates absent / corrupt storage
 * (private mode, cleared data) by returning an empty map.
 * @returns {Record<string, number>}
 */
function readMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** @param {Record<string, number>} map */
function writeMap(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (err) {
    console.warn("[learner] persist failed", err);
  }
}

/**
 * Record one training/test attempt. Correct → streak +1; wrong → streak reset
 * to 0. Mirrors `L2BItemRuntime.consecutiveCorrect` so the persisted streak
 * tracks the engine's in-session streak.
 * @param {number} mmuId
 * @param {boolean} correct
 */
export function recordAttempt(mmuId, correct) {
  if (!Number.isFinite(mmuId)) return;
  const map = readMap();
  const key = String(mmuId);
  const cur = map[key] ?? 0;
  map[key] = correct ? cur + 1 : 0;
  writeMap(map);
}

/**
 * MMU ids whose streak has reached the mastery target — the Testing pool.
 * @param {number} [masteryTarget=3]
 * @returns {number[]}
 */
export function getMasteredMmuIds(masteryTarget = DEFAULT_MASTERY_TARGET) {
  const map = readMap();
  /** @type {number[]} */ const out = [];
  for (const [key, streak] of Object.entries(map)) {
    if (streak >= masteryTarget) out.push(Number(key));
  }
  return out;
}

/**
 * Count of mastered MMUs (convenience for the dashboard / summaries).
 * @param {number} [masteryTarget=3]
 * @returns {number}
 */
export function getMasteredCount(masteryTarget = DEFAULT_MASTERY_TARGET) {
  return getMasteredMmuIds(masteryTarget).length;
}

/**
 * Clear the streaks for a specific set of MMUs (training "Practice again"). Pass
 * the exercise's full MMU id list so a replay starts every word from zero.
 * @param {number[]} mmuIds
 */
export function clearStreaks(mmuIds) {
  const map = readMap();
  for (const id of mmuIds) delete map[String(id)];
  writeMap(map);
}

/** Wipe all progress (demo reset hook). */
export function clearAll() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn("[learner] clear failed", err);
  }
}
